import { observer } from 'mobx-react'
import { type PropsWithChildren, useLayoutEffect } from 'react'
import { Navigate, useLocation } from 'react-router'

import { studentAppSettingLoader } from '~/@common/api/studentAppSettingLoader'
import { routeName } from '~/@common/constants'
import authService from '~/@common/services/auth.service'
import { commonRepo } from '~/@common/services/repo.service'
import { localStorageService, USER_STORAGE_KEYS } from '~/@common/services/storage.service'
import { InitialDataLoadFailure } from '~/@pages/@widgets/InitialDataLoadFailure'

const ProtectedRoute = ({ children }: PropsWithChildren) => {
  const { pathname, search } = useLocation()
  useLayoutEffect(() => {
    const studentId = commonRepo.studentId
    if (studentId && authService.isLoggedIn) {
      studentAppSettingLoader
        .getStudentAppSettings(studentId)
        .then((appSetting) => {
          // 학부모가 응답 전에 자녀를 바꿨다면 이전 자녀 설정으로 덮지 않는다.
          if (commonRepo.studentId !== studentId) return
          commonRepo.setStudent({ id: studentId, appSetting })
        })
        .catch((e) => {
          console.error(e)
        })
    }
  }, [commonRepo.studentId, authService.isLoggedIn, pathname])

  if (!authService.isLoggedIn) {
    const loginPath = localStorageService.loginPath ?? routeName.login
    // 로그인 후 원래 페이지로 돌아갈 수 있도록 현재 경로를 redirectURI로 전달 (일반 로그인 페이지만 소비)
    const redirectURI = pathname + search
    const loginUrl =
      loginPath === routeName.login && pathname && pathname !== '/' && pathname !== routeName.login
        ? `${routeName.login}?redirectURI=${encodeURIComponent(redirectURI)}`
        : loginPath
    return <Navigate to={loginUrl} replace />
  }

  // Fallback 트리거 — 두 갈래:
  //   (1) LS 식별자 자체가 없음 → 비정상 세션 (로그인 후 식별자 set 안 된 상태 등)
  //   (2) loadInitialData 가 실제 실패 (5xx / 네트워크 장애) — commonRepo.loadFailureReason 으로 감지
  //   fetch 진행 중에는 loadFailureReason 도 null 이라 children 렌더 → 깜빡임 ✗.
  //   commonRepo.student.schoolType 같은 응답 데이터 자체를 보면 fetch 진행 중에도 깜빡임 발생.
  const studentId = localStorage.getItem(USER_STORAGE_KEYS.studentId)
  const academyId = localStorage.getItem(USER_STORAGE_KEYS.academyId)
  const parentId = localStorage.getItem(USER_STORAGE_KEYS.parentId)
  const isMissingIdentity =
    authService.userType === 'STUDENT' ? !studentId || !academyId : !parentId
  const hasLoadFailure = !!commonRepo.loadFailureReason

  if (isMissingIdentity || hasLoadFailure) {
    return (
      <InitialDataLoadFailure
        errorMessage={commonRepo.loadFailureReason ?? '데이터를 불러오는 데 실패했어요'}
        onLogout={() => {
          void authService.logout()
        }}
      />
    )
  }

  return children
}
export default observer(ProtectedRoute)
